/**
 * Single source of truth for "will a visitor see this testimonial?".
 *
 * Mirrors the public RLS policy exactly:
 *   status = 'published'
 *   AND published_at IS NOT NULL
 *   AND consent_publish_quote = true
 *   AND signature_name IS NOT NULL AND char_length(btrim(signature_name)) >= 2
 *   AND signed_at IS NOT NULL
 *   AND release_effective_date <= today
 *   AND (release_expires_at IS NULL OR release_expires_at >= today)
 *
 * `asOf` lets the admin preview "time travel" to a future or past date to
 * see which releases open or expire then.
 */
export type TestimonialVisibilityInput = {
  status: string;
  published_at: string | null;
  consent_publish_quote: boolean;
  consent_publish_name: boolean;
  consent_publish_org: boolean;
  signature_name: string | null;
  signed_at: string | null;
  release_effective_date: string | null;
  release_expires_at: string | null;
};

export type VisibilityReason =
  | "not_published"
  | "missing_published_at"
  | "no_quote_consent"
  | "unsigned_release"
  | "no_effective_date"
  | "window_not_open"
  | "window_expired";

export const REASON_LABEL: Record<VisibilityReason, string> = {
  not_published: "Not published yet",
  missing_published_at: "Missing publish timestamp",
  no_quote_consent: "No consent to publish the quote",
  unsigned_release: "Release is unsigned",
  no_effective_date: "No release effective date",
  window_not_open: "Release window has not opened",
  window_expired: "Release window has expired",
};

export const todayISO = () => new Date().toISOString().slice(0, 10);

export function evaluateVisibility(
  row: TestimonialVisibilityInput,
  asOf: string = todayISO(),
): { visible: boolean; reasons: VisibilityReason[] } {
  const reasons: VisibilityReason[] = [];
  if (row.status !== "published") reasons.push("not_published");
  if (!row.published_at) reasons.push("missing_published_at");
  if (!row.consent_publish_quote) reasons.push("no_quote_consent");
  if (
    !row.signature_name ||
    row.signature_name.trim().length < 2 ||
    !row.signed_at
  ) {
    reasons.push("unsigned_release");
  }
  if (!row.release_effective_date) {
    reasons.push("no_effective_date");
  } else {
    if (row.release_effective_date > asOf) reasons.push("window_not_open");
    if (row.release_expires_at && row.release_expires_at < asOf) {
      reasons.push("window_expired");
    }
  }
  return { visible: reasons.length === 0, reasons };
}

/**
 * Attribution a visitor actually sees, after applying the name/org
 * consent flags (the quote itself is always consented when visible).
 */
export function publicAttribution(row: {
  full_name: string;
  organization: string | null;
  role_title: string | null;
  consent_publish_name: boolean;
  consent_publish_org: boolean;
}) {
  const name = row.consent_publish_name ? row.full_name : "Anonymous client";
  const org = row.consent_publish_org ? row.organization : null;
  const parts = [row.role_title, org].filter(Boolean) as string[];
  return { name, detail: parts.join(" · ") };
}
